import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChartBarIcon,
  UsersIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  Cog6ToothIcon,
  ArrowPathIcon,
  BookOpenIcon,
  PlayIcon,
  CalendarDaysIcon,
  ArrowTopRightOnSquareIcon,
  BanknotesIcon,
  MegaphoneIcon
} from '@heroicons/react/24/outline';
import {
  Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/apiClient';
import UserManagement from '../components/Admin/UserManagement';
import AdminCoupons from '../components/Admin/AdminCoupons';
import AdminSubscriptions from '../components/Admin/AdminSubscriptions';
import AdminFinance from '../components/Admin/AdminFinance';
import AdminBroadcasts from '../components/Admin/AdminBroadcasts';
import AdminCliqRequests from '../components/Admin/AdminCliqRequests';
import { formatAmount } from '../utils/currency';

// ─── Shared Components ────────────────────────────────────────────

const PERIOD_OPTIONS = [
  { value: 'all', label: 'All Time' },
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '90d', label: 'Last 3 Months' },
  { value: '1y', label: 'Last Year' },
  { value: 'custom', label: 'Custom Range' },
];

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];
const GAME_COLORS = {
  MIC: '#3B82F6', SKT: '#10B981', MEK: '#F59E0B', RMS: '#EF4444',
  ROT: '#8B5CF6', OWT: '#EC4899', PPT: '#06B6D4'
};

const COLOR_MAP = {
  blue:    { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-600 dark:text-blue-400' },
  green:   { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-600 dark:text-green-400' },
  indigo:  { bg: 'bg-indigo-50 dark:bg-indigo-900/20', text: 'text-indigo-600 dark:text-indigo-400' },
  emerald: { bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-600 dark:text-emerald-400' },
  purple:  { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-600 dark:text-purple-400' },
  amber:   { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-600 dark:text-amber-400' },
  red:     { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-600 dark:text-red-400' },
};

// ─── Currency ────────────────────────────────────────────────────
const CURRENCIES = {
  USD: { symbol: '$', rate: 1, label: 'USD' },
  EUR: { symbol: '€', rate: 0.92, label: 'EUR' },
  JOD: { symbol: 'JD', rate: 0.709, label: 'JOD' },
};

const convertCurrency = (usdAmount, currency) => {
  const cur = CURRENCIES[currency] || CURRENCIES.USD;
  return safe(usdAmount, 0) * cur.rate;
};

const safe = (v, fallback = 0) => (v !== undefined && v !== null ? v : fallback);
const safeFmt = (v) => safe(v, 0).toLocaleString();

const fmtMoney = (v, currency = 'USD') => {
  const cur = CURRENCIES[currency] || CURRENCIES.USD;
  const converted = convertCurrency(v, currency);
  return `${cur.symbol}${converted.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const StatCard = ({ title, value, subtitle, icon: Icon, color = 'blue', trend, trendLabel, onClick }) => {
  const c = COLOR_MAP[color] || COLOR_MAP.blue;
  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper
      onClick={onClick}
      className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow text-left w-full ${
        onClick ? 'cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 group' : ''
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider truncate">{title}</p>
          <p className="text-2xl font-bold mt-1 text-gray-900 dark:text-white">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>}
          {trend !== undefined && trend !== null && (
            <div className="flex items-center mt-2 gap-1">
              {trend >= 0 ? (
                <ArrowTrendingUpIcon className="h-3.5 w-3.5 text-green-500" />
              ) : (
                <ArrowTrendingDownIcon className="h-3.5 w-3.5 text-red-500" />
              )}
              <span className={`text-xs font-semibold ${trend >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {trend >= 0 ? '+' : ''}{trend}%
              </span>
              {trendLabel && <span className="text-xs text-gray-400">{trendLabel}</span>}
            </div>
          )}
        </div>
        <div className="flex flex-col items-center gap-1 flex-shrink-0">
          <div className={`p-2.5 rounded-lg ${c.bg}`}>
            <Icon className={`h-5 w-5 ${c.text}`} />
          </div>
          {onClick && (
            <ArrowTopRightOnSquareIcon className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
          )}
        </div>
      </div>
    </Wrapper>
  );
};

const ChartCard = ({ title, subtitle, children, className = '' }) => (
  <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 ${className}`}>
    <div className="mb-4">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
      {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const CustomTooltip = ({ active, payload, label, currency }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg px-3 py-2 shadow-lg border border-gray-600">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color }}>
          {entry.name}: {typeof entry.value === 'number' && entry.name?.toLowerCase().includes('revenue')
            ? fmtMoney(entry.value, currency || 'USD')
            : entry.value}
        </p>
      ))}
    </div>
  );
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

const formatDateLong = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// ─── OVERVIEW TAB ─────────────────────────────────────────────────

const OverviewTab = ({ data, currency, onNavigate }) => {
  if (!data) return null;
  const users = data.users || {};
  const content = data.content || {};
  const sales = data.sales || {};
  const recent_activity = data.recent_activity || {};

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard title="Total Users" value={safeFmt(users.total)} icon={UsersIcon} color="blue"
          trend={users.new_trend} trendLabel="vs prev period"
          onClick={() => onNavigate('management', 'users')} />
        <StatCard title="Active Users" value={safeFmt(users.active_period)} icon={ClockIcon} color="green"
          subtitle={`${safe(users.active_24h)} in last 24h`}
          onClick={() => onNavigate('users')} />
        <StatCard title="New Signups" value={safeFmt(users.new_period)} icon={ArrowTrendingUpIcon} color="indigo"
          trend={users.new_trend}
          onClick={() => onNavigate('users')} />
        <StatCard title="Revenue" value={fmtMoney(sales.revenue_period, currency)}
          icon={CurrencyDollarIcon} color="emerald" trend={sales.revenue_trend}
          onClick={() => onNavigate('sales')} />
        <StatCard title="Purchases" value={safeFmt(sales.purchases_period)} icon={DocumentTextIcon} color="purple"
          subtitle={`${safe(sales.active_subscriptions)} active subs`}
          onClick={() => onNavigate('management', 'subscriptions')} />
        <StatCard title="Avg Score" value={`${safe(content.avg_score)}%`} icon={ChartBarIcon} color="amber"
          subtitle={`${safe(content.exams_completed)} exams completed`}
          onClick={() => onNavigate('content')} />
      </div>

      {/* Main Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ChartCard title="User Growth" subtitle="Daily signups & logins">
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={(users.signups_series || []).map((s, i) => ({
              date: formatDate(s.date),
              signups: s.count,
              logins: (users.logins_series || [])[i]?.count || 0
            }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#9CA3AF" />
              <YAxis tick={{ fontSize: 10 }} stroke="#9CA3AF" />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="signups" name="Signups" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.15} />
              <Area type="monotone" dataKey="logins" name="Logins" stroke="#10B981" fill="#10B981" fillOpacity={0.1} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Revenue Trend" subtitle="Daily revenue">
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={(sales.revenue_series || []).map(s => ({
              date: formatDate(s.date),
              revenue: s.total || 0
            }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#9CA3AF" />
              <YAxis tick={{ fontSize: 10 }} stroke="#9CA3AF" tickFormatter={v => `${CURRENCIES[currency]?.symbol || '$'}${v}`} />
              <Tooltip content={<CustomTooltip currency={currency} />} />
              <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#10B981" fill="#10B981" fillOpacity={0.2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Recent Signups */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Recent Signups</h3>
          <div className="space-y-2.5">
            {(recent_activity.signups || []).slice(0, 6).map(u => (
              <div key={u.id} className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {u.first_name} {u.last_name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{u.email}</p>
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                  {formatDateLong(u.date_joined)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Purchases */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Recent Purchases</h3>
          <div className="space-y-2.5">
            {(recent_activity.purchases || []).slice(0, 6).map(p => (
              <div key={p.id} className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {formatAmount(p.amount_paid, p.currency)} — {p.pricing_tier__course__name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{p.user__email}</p>
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                  {formatDateLong(p.purchased_at)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Exams */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Recent Exams</h3>
          <div className="space-y-2.5">
            {(recent_activity.exams || []).slice(0, 6).map(e => (
              <div key={e.id} className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {e.subject__name} ({e.total_questions}Q)
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{e.user__email}</p>
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                  {e.score_percentage !== null && (
                    <span className={`text-xs font-semibold ${e.score_percentage >= 75 ? 'text-green-500' : 'text-red-500'}`}>
                      {e.score_percentage}%
                    </span>
                  )}
                  <p className="text-xs text-gray-400">{e.status}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── USERS TAB ────────────────────────────────────────────────────

const UsersTab = ({ data, onNavigate }) => {
  if (!data) return null;
  const users = data.users || {};

  // Merge signups + logins into one series
  const userActivityData = (users.signups_series || []).map((s, i) => ({
    date: formatDate(s.date),
    signups: s.count,
    logins: (users.logins_series || [])[i]?.count || 0
  }));

  // Region data for pie chart
  const regionData = (users.by_region || []).filter(r => r.count > 0).map(r => ({
    name: r.region || 'Not specified',
    value: r.count
  }));

  return (
    <div className="space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard title="Total Users" value={safeFmt(users.total)} icon={UsersIcon} color="blue"
          onClick={() => onNavigate('management', 'users')} />
        <StatCard title="New Users" value={safeFmt(users.new_period)} icon={ArrowTrendingUpIcon} color="indigo"
          trend={users.new_trend} onClick={() => onNavigate('management', 'users')} />
        <StatCard title="Active" value={safeFmt(users.active_period)} icon={ClockIcon} color="green"
          subtitle={`${safe(users.active_24h)} in 24h`} onClick={() => onNavigate('management', 'users')} />
        <StatCard title="Inactive" value={safeFmt(users.inactive)} icon={ClockIcon} color="red"
          onClick={() => onNavigate('management', 'users')} />
        <StatCard title="Conversion" value={`${safe(users.conversion_rate)}%`} icon={CurrencyDollarIcon} color="emerald"
          subtitle="Registered → Purchased" onClick={() => onNavigate('management', 'subscriptions')} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <ChartCard title="Daily Signups vs Logins" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={userActivityData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#9CA3AF" />
              <YAxis tick={{ fontSize: 10 }} stroke="#9CA3AF" />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="signups" name="Signups" stroke="#6366F1" fill="#6366F1" fillOpacity={0.2} />
              <Area type="monotone" dataKey="logins" name="Logins" stroke="#10B981" fill="#10B981" fillOpacity={0.1} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Users by Region">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={regionData} cx="50%" cy="50%" innerRadius={50} outerRadius={90}
                paddingAngle={2} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                labelLine={false}>
                {regionData.map((_, idx) => (
                  <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* User Management Table */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">User Management</h3>
        <UserManagement />
      </div>
    </div>
  );
};

// ─── CONTENT TAB ──────────────────────────────────────────────────

const ContentTab = ({ data, onNavigate }) => {
  if (!data) return null;
  const content = data.content || {};

  const questionsData = (content.questions_series || []).map(s => ({
    date: formatDate(s.date),
    questions: s.count
  }));

  const examsData = (content.exams_series || []).map(s => ({
    date: formatDate(s.date),
    exams: s.count
  }));

  const gameRunsData = (content.game_runs_series || []).map(s => ({
    date: formatDate(s.date),
    ...Object.fromEntries(Object.keys(GAME_COLORS).map(g => [g, s[g] || 0])),
    total: s.total || 0
  }));

  // Game runs breakdown for bar chart
  const gameBreakdown = Object.entries(content.game_runs_by_type || {})
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);

  return (
    <div className="space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard title="Questions Answered" value={safeFmt(content.questions_answered)} icon={DocumentTextIcon} color="blue" />
        <StatCard title="Exams Created" value={safeFmt(content.exams_created)} icon={BookOpenIcon} color="indigo"
          subtitle={`${safe(content.exams_completed)} completed`} />
        <StatCard title="Game Runs" value={safeFmt(content.game_runs_total)} icon={PlayIcon} color="purple" />
        <StatCard title="Avg Score" value={`${safe(content.avg_score)}%`} icon={ChartBarIcon} color="amber"
          onClick={() => onNavigate('content')} />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ChartCard title="Questions Answered Per Day">
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={questionsData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#9CA3AF" />
              <YAxis tick={{ fontSize: 10 }} stroke="#9CA3AF" />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="questions" name="Questions" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Exams Created Per Day">
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={examsData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#9CA3AF" />
              <YAxis tick={{ fontSize: 10 }} stroke="#9CA3AF" />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="exams" name="Exams" stroke="#6366F1" fill="#6366F1" fillOpacity={0.2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <ChartCard title="Game Runs Per Day" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={gameRunsData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#9CA3AF" />
              <YAxis tick={{ fontSize: 10 }} stroke="#9CA3AF" />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              {Object.entries(GAME_COLORS).map(([game, color]) => (
                <Bar key={game} dataKey={game} name={game} stackId="games" fill={color} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Game Runs Breakdown">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={gameBreakdown} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis type="number" tick={{ fontSize: 10 }} stroke="#9CA3AF" />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} stroke="#9CA3AF" width={40} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" name="Runs" radius={[0, 4, 4, 0]}>
                {gameBreakdown.map((entry, idx) => (
                  <Cell key={idx} fill={GAME_COLORS[entry.name] || COLORS[idx]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Subject Popularity */}
      {(content.subject_popularity || []).length > 0 && (
        <ChartCard title="Subject Popularity" subtitle="Exams created by subject">
          <ResponsiveContainer width="100%" height={Math.max(200, (content.subject_popularity || []).length * 32)}>
            <BarChart data={content.subject_popularity || []} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis type="number" tick={{ fontSize: 10 }} stroke="#9CA3AF" />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} stroke="#9CA3AF" width={180} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" name="Exams" fill="#6366F1" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}
    </div>
  );
};

// ─── SALES TAB ────────────────────────────────────────────────────

const SalesTab = ({ data, currency, onNavigate }) => {
  if (!data) return null;
  const sales = data.sales || {};

  const revenueData = (sales.revenue_series || []).map(s => ({
    date: formatDate(s.date),
    revenue: s.total || 0,
    purchases: 0
  }));
  // Merge purchase counts
  (sales.purchases_series || []).forEach((s, i) => {
    if (revenueData[i]) revenueData[i].purchases = s.count;
  });

  // Coupon series
  const couponData = (sales.coupons_created_series || []).map((s, i) => ({
    date: formatDate(s.date),
    created: s.count,
    used: (sales.coupons_used_series || [])[i]?.count || 0
  }));

  return (
    <div className="space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard title="Revenue (All Time)" value={fmtMoney(sales.total_revenue_all_time, currency)}
          icon={CurrencyDollarIcon} color="emerald" onClick={() => onNavigate('management', 'subscriptions')} />
        <StatCard title="Revenue (Period)" value={fmtMoney(sales.revenue_period, currency)}
          icon={CurrencyDollarIcon} color="green" trend={sales.revenue_trend} onClick={() => onNavigate('management', 'subscriptions')} />
        <StatCard title="Purchases" value={safeFmt(sales.purchases_period)} icon={DocumentTextIcon} color="purple"
          onClick={() => onNavigate('management', 'subscriptions')} />
        <StatCard title="Active Subs" value={safeFmt(sales.active_subscriptions)} icon={ClockIcon} color="blue"
          onClick={() => onNavigate('management', 'subscriptions')} />
        <StatCard title="Coupons Used" value={`${safe(sales.coupons_used)}/${safe(sales.coupons_created)}`} icon={DocumentTextIcon} color="amber"
          subtitle={`${safe(sales.coupons_active)} active`} onClick={() => onNavigate('management', 'coupons')} />
        <StatCard title="ARPU" value={fmtMoney(sales.arpu, currency)} icon={CurrencyDollarIcon} color="indigo"
          subtitle="Avg Rev Per User" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ChartCard title="Revenue & Purchases Per Day">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#9CA3AF" />
              <YAxis yAxisId="revenue" tick={{ fontSize: 10 }} stroke="#9CA3AF" tickFormatter={v => `${CURRENCIES[currency]?.symbol || '$'}${v}`} />
              <YAxis yAxisId="purchases" orientation="right" tick={{ fontSize: 10 }} stroke="#9CA3AF" />
              <Tooltip content={<CustomTooltip currency={currency} />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area yAxisId="revenue" type="monotone" dataKey="revenue" name="Revenue" stroke="#10B981" fill="#10B981" fillOpacity={0.2} />
              <Line yAxisId="purchases" type="monotone" dataKey="purchases" name="Purchases" stroke="#8B5CF6" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Coupon Creation vs Redemption">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={couponData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#9CA3AF" />
              <YAxis tick={{ fontSize: 10 }} stroke="#9CA3AF" />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="created" name="Created" fill="#F59E0B" />
              <Bar dataKey="used" name="Used" fill="#10B981" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Purchases per Course */}
      {(sales.purchases_per_course || []).length > 0 && (
        <ChartCard title="Purchases Per Course" subtitle="Revenue and count by course">
          <ResponsiveContainer width="100%" height={Math.max(200, (sales.purchases_per_course || []).length * 40)}>
            <BarChart data={sales.purchases_per_course || []} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis type="number" tick={{ fontSize: 10 }} stroke="#9CA3AF" />
              <YAxis dataKey="course_name" type="category" tick={{ fontSize: 10 }} stroke="#9CA3AF" width={120} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="count" name="Purchases" fill="#8B5CF6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* Payment Status Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatCard title="Completed Payments" value={safeFmt(sales.total_payments)} icon={DocumentTextIcon} color="green"
          onClick={() => onNavigate('management', 'subscriptions')} />
        <StatCard title="Pending Payments" value={safeFmt(sales.pending_payments)} icon={ClockIcon} color="amber"
          onClick={() => onNavigate('management', 'subscriptions')} />
        <StatCard title="Failed Payments" value={safeFmt(sales.failed_payments)} icon={ExclamationTriangleIcon} color="red"
          onClick={() => onNavigate('management', 'subscriptions')} />
      </div>
    </div>
  );
};

// ─── MANAGEMENT TAB ───────────────────────────────────────────────

const ManagementTab = ({ initialSubTab }) => {
  const [subTab, setSubTab] = useState(initialSubTab || 'users');

  useEffect(() => {
    if (initialSubTab) setSubTab(initialSubTab);
  }, [initialSubTab]);

  const subTabs = [
    { id: 'users', label: 'Users', icon: UsersIcon },
    { id: 'coupons', label: 'Coupons', icon: DocumentTextIcon },
    { id: 'subscriptions', label: 'Subscriptions', icon: ClockIcon },
    { id: 'cliq', label: 'CliQ Subscriptions', icon: BanknotesIcon },
    { id: 'broadcasts', label: 'Broadcasts', icon: MegaphoneIcon },
  ];

  return (
    <div className="space-y-4">
      {/* Sub-tabs */}
      <div className="flex gap-1 bg-gray-100 dark:bg-gray-700/50 rounded-lg p-1 w-fit">
        {subTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setSubTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              subTab === tab.id
                ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {subTab === 'users' && <UserManagement />}
      {subTab === 'coupons' && <AdminCoupons />}
      {subTab === 'subscriptions' && <AdminSubscriptions />}
      {subTab === 'cliq' && <AdminCliqRequests />}
      {subTab === 'broadcasts' && <AdminBroadcasts />}
    </div>
  );
};

// ─── MAIN ADMIN PANEL ─────────────────────────────────────────────

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [currency, setCurrency] = useState('JOD');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [managementSubTab, setManagementSubTab] = useState('users');

  // Admin check
  useEffect(() => {
    if (!user?.is_staff && !user?.is_superuser) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const buildQueryString = useCallback(() => {
    if (period === 'custom' && customStart && customEnd) {
      return `start_date=${customStart}&end_date=${customEnd}`;
    }
    return `period=${period}`;
  }, [period, customStart, customEnd]);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/api/auth/admin/dashboard/?${buildQueryString()}`);
      setData(response);
    } catch (err) {
      console.error('Failed to fetch admin dashboard:', err);
      if (err.response?.status === 403) {
        setError('You do not have permission to access admin statistics.');
      } else {
        setError(err.message || 'Failed to load dashboard data.');
      }
    } finally {
      setLoading(false);
    }
  }, [buildQueryString]);

  useEffect(() => {
    if (activeTab !== 'management') {
      fetchDashboard();
    }
  }, [fetchDashboard, activeTab]);

  const handlePeriodChange = (val) => {
    if (val === 'custom') {
      setShowDatePicker(true);
      setPeriod('custom');
    } else {
      setShowDatePicker(false);
      setPeriod(val);
    }
  };

  const handleCustomDateApply = () => {
    if (customStart && customEnd) {
      fetchDashboard();
    }
  };

  // Navigation handler: switches tab (and optionally sub-tab for management)
  const handleNavigate = (tab, subTab) => {
    setActiveTab(tab);
    if (tab === 'management' && subTab) {
      setManagementSubTab(subTab);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: ChartBarIcon },
    { id: 'users', label: 'Users', icon: UsersIcon },
    { id: 'content', label: 'Content', icon: BookOpenIcon },
    { id: 'sales', label: 'Sales', icon: CurrencyDollarIcon },
    { id: 'finance', label: 'Finance', icon: BanknotesIcon },
    { id: 'management', label: 'Management', icon: Cog6ToothIcon },
  ];

  const hideControls = activeTab === 'management' || activeTab === 'finance';

  return (
    <div className="w-full max-w-[1400px] mx-auto space-y-5 p-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Admin Panel</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Platform analytics & management</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!hideControls && (
            <>
              {/* Currency Switcher */}
              <div className="flex items-center bg-gray-100 dark:bg-gray-700/50 rounded-lg p-0.5">
                {Object.keys(CURRENCIES).map(cur => (
                  <button
                    key={cur}
                    onClick={() => setCurrency(cur)}
                    className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${
                      currency === cur
                        ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                    }`}
                  >
                    {CURRENCIES[cur].symbol} {cur}
                  </button>
                ))}
              </div>

              {/* Period Select */}
              <select
                value={period}
                onChange={(e) => handlePeriodChange(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              >
                {PERIOD_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </>
          )}
          <button
            onClick={fetchDashboard}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 transition-colors"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Custom Date Range Picker */}
      {showDatePicker && !hideControls && (
        <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <CalendarDaysIcon className="h-5 w-5 text-blue-500 flex-shrink-0" />
          <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">From</span>
          <input
            type="date"
            value={customStart}
            onChange={(e) => setCustomStart(e.target.value)}
            className="px-2.5 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">To</span>
          <input
            type="date"
            value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)}
            className="px-2.5 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
          <button
            onClick={handleCustomDateApply}
            disabled={!customStart || !customEnd}
            className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 transition-colors"
          >
            Apply
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading && !data ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">Loading dashboard...</p>
          </div>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mb-3" />
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
            Go to Dashboard
          </button>
        </div>
      ) : (
        <>
          {activeTab === 'overview' && <OverviewTab data={data} currency={currency} onNavigate={handleNavigate} />}
          {activeTab === 'users' && <UsersTab data={data} onNavigate={handleNavigate} />}
          {activeTab === 'content' && <ContentTab data={data} onNavigate={handleNavigate} />}
          {activeTab === 'sales' && <SalesTab data={data} currency={currency} onNavigate={handleNavigate} />}
          {activeTab === 'finance' && <AdminFinance period={period} customStart={customStart} customEnd={customEnd} />}
          {activeTab === 'management' && <ManagementTab initialSubTab={managementSubTab} />}
        </>
      )}
    </div>
  );
};

export default AdminPanel;
